import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../WebApp';

const MEDIA_BASE =
  process.env.NODE_ENV === 'production'
    ? 'https://hotbump.online'
    : 'http://localhost:8010';

const resolveMediaUrl = (url) => {
  if (!url) return '/avatars/default.png';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${MEDIA_BASE}${url}`;
};

function GroupChat() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user, language, updateUser } = React.useContext(AuthContext);

  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const hasTokens = typeof user?.token_balance === 'number' && user.token_balance >= 1;
  const [forcing, setForcing] = useState(false);
  const [input, setInput] = useState('');
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [rightTab, setRightTab] = useState('models');
  const [groupList, setGroupList] = useState([]);
  const [groupLoading, setGroupLoading] = useState(true);

  const translations = {
    en: {
      title: 'Group chat',
      send: 'Send',
      sending: 'Sending...',
      notYourTurn: "It's not your turn",
      force: 'Force reply',
      currentTurn: 'Current turn',
      tokenShort: 'Not enough tokens',
      back: 'Back to chats',
      chats: 'Chats',
      models: 'Models',
      media: 'Media',
      openMedia: 'Open media',
      loadingList: 'Loading chats...',
      noChats: 'No chats yet.',
    },
    ru: {
      title: 'Групповой чат',
      send: 'Отправить',
      sending: 'Отправка...',
      notYourTurn: 'Сейчас не ваш ход',
      force: 'Заставить ответить',
      currentTurn: 'Текущий ход',
      tokenShort: 'Недостаточно токенов',
      back: 'Назад к чатам',
      chats: 'Чаты',
      models: 'Модели',
      media: 'Медиа',
      openMedia: 'Открыть медиа',
      loadingList: 'Загрузка чатов...',
      noChats: 'Чатов пока нет.',
    },
  };
  const t = translations[language] || translations.en;

  const loadChat = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/web/group-chats/${chatId}`);
      setChat(response.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || (language === 'en' ? 'Failed to load chat' : 'Не удалось загрузить чат');
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [chatId, language]);

  useEffect(() => {
    loadChat();
  }, [loadChat]);

  useEffect(() => {
    const loadGroupList = async () => {
      if (!user) {
        setGroupList([]);
        setGroupLoading(false);
        return;
      }
      setGroupLoading(true);
      try {
        const response = await axios.get('/web/group-chats');
        setGroupList(response.data?.chats || []);
      } catch (err) {
        setGroupList([]);
      } finally {
        setGroupLoading(false);
      }
    };
    loadGroupList();
  }, [user]);

  const isUserTurn = chat?.current_turn
    && chat.participants?.some((p) => p.key === chat.current_turn && p.type === 'user' && p.user_id === user?.id);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (!isUserTurn) {
      setError(t.notYourTurn);
      return;
    }
    if (user && typeof user.token_balance === 'number' && user.token_balance < 1) {
      navigate('/web/promo');
      return;
    }
    setSending(true);
    setError('');
    try {
      const response = await axios.post(`/web/group-chats/${chatId}/message`, { content: input });
      if (response?.data) {
        setChat(response.data);
        if (typeof response.data.token_balance === 'number' && updateUser) {
          updateUser({ ...(user || {}), token_balance: response.data.token_balance });
        }
      }
      setInput('');
    } catch (err) {
      const detail = err?.response?.data?.detail || (language === 'en' ? 'Failed to send message' : 'Не удалось отправить сообщение');
      setError(detail);
    } finally {
      setSending(false);
    }
  };

  const forceModel = async (participantKey) => {
    if (!participantKey) return;
    setForcing(true);
    setError('');
    try {
      const response = await axios.post(`/web/group-chats/${chatId}/force-turn`, { participant_key: participantKey });
      if (response?.data) {
        setChat(response.data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || (language === 'en' ? 'Failed to trigger model' : 'Не удалось вызвать модель');
      setError(detail);
    } finally {
      setForcing(false);
    }
  };

  const renderParticipants = () => {
    if (!chat?.participants) return null;
    return (
      <div className="flex flex-wrap gap-3">
        {chat.participants.map((p) => (
          <div
            key={p.key}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${p.is_current_turn ? 'border-burgundy-500 bg-burgundy-900/30' : 'border-white/10 bg-black/30'}`}
          >
            <div className="h-10 w-10 rounded-full overflow-hidden bg-dark-700 border border-white/10 flex items-center justify-center">
              {p.avatar_url ? (
                <img src={p.avatar_url} alt={p.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-sm text-gray-300">{p.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold">{p.name}</div>
              <div className="text-xs text-gray-400">
                {p.type === 'user' ? (language === 'en' ? 'User' : 'Пользователь') : (language === 'en' ? 'Model' : 'Модель')}
              </div>
            </div>
            {p.type === 'character' && (
              <button
                type="button"
                onClick={() => forceModel(p.key)}
                disabled={forcing}
                className="ml-2 text-xs text-burgundy-200 hover:text-white underline"
              >
                {t.force}
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderMessages = () => {
    if (!chat?.messages?.length) {
      return (
        <div className="text-gray-400 text-center py-10">
          {language === 'en' ? 'No messages yet.' : 'Сообщений пока нет.'}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {chat.messages.map((m) => (
          <div
            key={m.id}
            className={`rounded-2xl px-4 py-3 border ${m.sender_type === 'user' ? 'bg-burgundy-900/40 border-burgundy-500/40' : 'bg-black/40 border-white/10'}`}
          >
            <div className="flex items-center justify-between text-sm text-gray-300 mb-1">
              <span className="font-semibold">{m.sender_name || m.sender_ref}</span>
              {m.created_at && (
                <span className="text-xs text-gray-500">{new Date(m.created_at).toLocaleString()}</span>
              )}
            </div>
            <p className="text-white whitespace-pre-line">{m.content}</p>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center text-gray-300 py-16">
        {language === 'en' ? 'Loading group chat...' : 'Загрузка группового чата...'}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-300 py-16 space-y-3">
        <div>{error}</div>
        <button
          type="button"
          onClick={loadChat}
          className="px-4 py-2 rounded-lg bg-burgundy-600 text-white hover:bg-burgundy-500"
        >
          {language === 'en' ? 'Retry' : 'Повторить'}
        </button>
      </div>
    );
  }

  const modelParticipants = (chat?.participants || []).filter((p) => p.type === 'character');
  const bgImages = modelParticipants.map((p) => resolveMediaUrl(p.avatar_url));

  return (
    <div className="relative min-h-[calc(100vh-80px)] text-white overflow-hidden">
      <div className="absolute inset-0 flex">
        {bgImages.length === 0 ? (
          <div className="flex-1 bg-gradient-to-br from-black via-dark-900 to-burgundy-900" />
        ) : (
          bgImages.map((src, idx) => (
            <div
              key={src + idx}
              className="flex-1 relative"
              style={{
                backgroundImage: `url(${src})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'brightness(0.35)',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
            </div>
          ))
        )}
      </div>

      <div className="relative z-10 flex h-full">
        <aside className={`${leftCollapsed ? 'w-14' : 'w-64'} transition-all duration-200 bg-black/50 border-r border-white/10 backdrop-blur-lg flex flex-col`}>
          <div className="flex items-center justify-between px-3 py-3 border-b border-white/10">
            {!leftCollapsed && <div className="text-sm font-semibold">{t.chats}</div>}
            <button
              type="button"
              onClick={() => setLeftCollapsed((v) => !v)}
              className="text-lg text-gray-300 hover:text-white"
              aria-label="Toggle chats"
            >
              {leftCollapsed ? '›' : '‹'}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {groupLoading ? (
              <div className="text-gray-400 text-sm p-3">{t.loadingList}</div>
            ) : groupList.length === 0 ? (
              <div className="text-gray-400 text-sm p-3">{t.noChats}</div>
            ) : (
              groupList.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(`/web/group-chat/${item.id}`)}
                  className={`w-full text-left px-3 py-3 border-b border-white/5 hover:bg-white/5 ${item.id === Number(chatId) ? 'bg-white/10' : ''}`}
                >
                  <div className="text-sm font-semibold truncate">
                    {item.title || t.title}
                  </div>
                  {!leftCollapsed && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.participants?.slice(0, 3).map((p) => (
                        <span key={p.key} className="text-[11px] text-gray-400 truncate max-w-[120px]">
                          {p.name}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </aside>

        <section className="flex-1 min-h-full flex flex-col px-3 sm:px-6 py-4 gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-white drop-shadow">{t.title}</h1>
              <p className="text-gray-200 text-sm mt-1">
                {language === 'en' ? 'Carousel: choose who speaks next' : 'Карусель: задайте порядок сообщений'}
              </p>
              {chat?.current_turn && (
                <div className="text-sm text-burgundy-200 mt-1">
                  {t.currentTurn}:{' '}
                  {chat.participants?.find((p) => p.key === chat.current_turn)?.name || chat.current_turn}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate('/web/my-chat')}
              className="px-4 py-2 rounded-xl border border-white/20 text-gray-200 hover:bg-white/10 backdrop-blur"
            >
              {t.back}
            </button>
          </div>

          <div className="bg-black/55 border border-white/10 rounded-2xl p-3 backdrop-blur">
            {renderParticipants()}
          </div>

          <div className="flex-1 min-h-[320px] bg-black/55 border border-white/10 rounded-2xl p-4 backdrop-blur overflow-y-auto">
            {renderMessages()}
          </div>

          <form onSubmit={handleSend} className="space-y-3">
            {error && (
              <div className="text-sm text-red-300 bg-red-900/30 border border-red-600/30 px-3 py-2 rounded-xl">
                {error}
              </div>
            )}
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={3}
              className="w-full rounded-2xl bg-black/70 border border-white/10 text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-burgundy-500 backdrop-blur"
              placeholder={language === 'en' ? 'Type your message...' : 'Введите сообщение...'}
            />
            <div className="flex items-center justify-end">
              <button
                type="submit"
                disabled={!isUserTurn || sending || !input.trim() || !hasTokens}
                onClick={(e) => {
                  if (!hasTokens) {
                    e.preventDefault();
                    navigate('/web/promo');
                    return;
                  }
                }}
                className="px-5 py-2 rounded-xl bg-burgundy-600 hover:bg-burgundy-500 text-white font-semibold disabled:opacity-50"
                title={
                  !hasTokens
                    ? (language === 'en' ? 'Top up tokens to send messages' : 'Пополните баланс, чтобы отправлять сообщения')
                    : undefined
                }
              >
                {sending ? t.sending : t.send}
              </button>
            </div>
          </form>
        </section>

        <aside className={`${rightCollapsed ? 'w-14' : 'w-72'} transition-all duration-200 bg-black/50 border-l border-white/10 backdrop-blur-lg flex flex-col`}>
          <div className="flex items-center justify-between px-3 py-3 border-b border-white/10">
            {!rightCollapsed && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRightTab('models')}
                  className={`text-sm px-3 py-1 rounded-full ${rightTab === 'models' ? 'bg-burgundy-600 text-white' : 'text-gray-300 bg-white/5'}`}
                >
                  {t.models}
                </button>
                <button
                  type="button"
                  onClick={() => setRightTab('media')}
                  className={`text-sm px-3 py-1 rounded-full ${rightTab === 'media' ? 'bg-burgundy-600 text-white' : 'text-gray-300 bg-white/5'}`}
                >
                  {t.media}
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => setRightCollapsed((v) => !v)}
              className="text-lg text-gray-300 hover:text-white"
              aria-label="Toggle models"
            >
              {rightCollapsed ? '‹' : '›'}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {rightTab === 'models' ? (
              modelParticipants.length === 0 ? (
                <div className="text-gray-400 text-sm">{language === 'en' ? 'No models' : 'Модели отсутствуют'}</div>
              ) : (
                modelParticipants.map((p) => (
                  <div key={p.key} className="rounded-xl border border-white/10 bg-black/30 p-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={resolveMediaUrl(p.avatar_url)}
                        alt={p.name}
                        className="h-12 w-12 rounded-xl object-cover border border-white/10"
                      />
                      <div>
                        <div className="font-semibold text-white text-sm">{p.name}</div>
                        <div className="text-xs text-gray-400">{language === 'en' ? 'Model' : 'Модель'}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/web/chat/${p.character_id}`)}
                      className="mt-3 w-full px-3 py-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-500 text-white text-sm font-semibold"
                    >
                      {t.openMedia}
                    </button>
                  </div>
                ))
              )
            ) : (
              <div className="text-sm text-gray-300 rounded-xl border border-white/10 bg-black/30 p-3">
                {language === 'en'
                  ? 'Media is available inside each model page. Open a model to browse photos/videos.'
                  : 'Медиа доступно внутри страницы модели. Откройте модель, чтобы посмотреть фото/видео.'}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export default GroupChat;

